import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Modal } from "@/pages/Dashboard";
import { Loader2, Copy, Trash2, FileText, ClipboardCheck, Target, Check, Clock, ExternalLink, Sparkles, FileBarChart2, Layers } from "lucide-react";

const DOC_TYPES = [
  { key: "parent_report", label: "Parent Report", Icon: FileBarChart2 },
  { key: "cos_plan", label: "CoS Plan", Icon: Layers },
  { key: "cos_review", label: "CoS Review", Icon: Layers },
  { key: "target_sheet", label: "Priority Targets", Icon: Target },
  { key: "plan", label: "Support Plan", Icon: FileText },
  { key: "review", label: "Support Review", Icon: ClipboardCheck },
  { key: "summary", label: "AI Summary", Icon: Sparkles },
];

function ShareStatusBadge({ signed, expired }) {
  if (signed) return <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5"><Check size={12} /> Signed</span>;
  if (expired) return <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-red-700 bg-red-50 rounded-full px-2 py-0.5"><Clock size={12} /> Expired</span>;
  return <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 rounded-full px-2 py-0.5"><Clock size={12} /> Awaiting signature</span>;
}

function ShareRow({ share: s, onCopy, onRevoke }) {
  const signed = s.status === "signed";
  return (
    <div data-testid={`share-row-${s.id}`} className="rounded-xl border border-slate-100 p-3.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold font-['Outfit'] text-slate-800 text-sm">{s.doc_title}</span>
        <ShareStatusBadge signed={signed} expired={s.expired} />
        <span className="text-xs text-slate-400 ml-auto">{new Date(s.created_at).toLocaleDateString("en-IE", { day: "numeric", month: "short" })}</span>
      </div>

      {!signed && s.expires_at && (
        <p className={`text-xs mt-1 ${s.expired ? "text-red-500" : "text-slate-400"}`}>
          {s.expired ? "Expired " : "Expires "}{new Date(s.expires_at).toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "numeric" })}
        </p>
      )}

      {signed && s.signature && (
        <div className="mt-2 rounded-lg bg-[#F9F9F7] border border-slate-100 p-2.5 text-sm">
          <p className="text-slate-700">Signed by <b>{s.signature.parent_name}</b> · {new Date(s.signature.signed_at).toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "numeric" })}</p>
          {s.signature.comment && <p className="text-slate-500 italic mt-1">“{s.signature.comment}”</p>}
        </div>
      )}

      <div className="flex items-center gap-2 mt-2.5">
        {s.access_code && (
          <span data-testid={`share-code-${s.id}`} className="text-xs font-semibold rounded-full bg-[#F2F2F2] text-slate-600 px-3 py-1.5">
            Code <b className="tracking-widest text-slate-800">{s.access_code}</b>
          </span>
        )}
        <button data-testid={`copy-share-${s.id}`} onClick={() => onCopy(s.token)}
          className="inline-flex items-center gap-1 text-xs font-semibold rounded-full border border-slate-200 px-3 py-1.5 text-slate-600 hover:border-[#7FB069] transition">
          <Copy size={13} /> Copy link
        </button>
        <a data-testid={`open-share-${s.id}`} href={`/p/${s.token}`} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs font-semibold rounded-full border border-slate-200 px-3 py-1.5 text-slate-600 hover:border-[#7FB069] transition">
          <ExternalLink size={13} /> {signed ? "View signed" : "Preview"}
        </a>
        <button data-testid={`revoke-share-${s.id}`} onClick={() => onRevoke(s.id)}
          className="ml-auto text-slate-300 hover:text-red-500 transition"><Trash2 size={15} /></button>
      </div>
    </div>
  );
}

export default function ParentShareModal({ studentId, studentName, onClose }) {
  const [shares, setShares] = useState(null);
  const [creating, setCreating] = useState("");
  const defaultExpiry = () => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); };
  const [expiry, setExpiry] = useState(defaultExpiry());

  const load = () => api.get(`/parent-shares?student_id=${studentId}`).then((r) => setShares(r.data)).catch(() => setShares([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [studentId]);

  const linkFor = (token) => `${window.location.origin}/p/${token}`;
  const copy = async (token) => { await navigator.clipboard.writeText(linkFor(token)); toast.success("Parent link copied"); };

  const create = async (docType) => {
    setCreating(docType);
    try {
      const { data } = await api.post("/parent-shares", { student_id: studentId, doc_type: docType, expires_at: expiry });
      await navigator.clipboard.writeText(linkFor(data.token)).catch(() => {});
      toast.success(`Link copied — share it with the parent. Access code: ${data.access_code}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCreating("");
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this link? Parents will no longer be able to open it.")) return;
    await api.delete(`/parent-shares/${id}`);
    toast.success("Link revoked");
    load();
  };

  return (
    <Modal title="Share with parents" onClose={onClose}>
      <p className="text-sm text-slate-500 -mt-2 mb-4">
        Create a secure link to share a document for <b>{studentName}</b> to read, comment on and sign. The signed copy is saved back here.
      </p>

      <div className="flex items-center justify-between gap-3 mb-3 rounded-xl bg-[#F9F9F7] border border-slate-100 px-3.5 py-2.5">
        <label htmlFor="share-expiry" className="text-sm font-semibold text-slate-600 font-['Outfit']">Link expires</label>
        <input id="share-expiry" data-testid="share-expiry-input" type="date" value={expiry}
          min={new Date().toISOString().slice(0, 10)} onChange={(e) => setExpiry(e.target.value)}
          className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700 outline-none focus:border-[#7FB069]" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-5">
        {DOC_TYPES.map(({ key, label, Icon }) => (
          <button key={key} data-testid={`create-share-${key}`} onClick={() => create(key)} disabled={creating === key}
            className="flex flex-col items-center gap-1.5 rounded-xl border border-slate-200 hover:border-[#7FB069] py-3 px-2 text-center transition disabled:opacity-60">
            {creating === key ? <Loader2 size={18} className="animate-spin text-[#5d8a73]" /> : <Icon size={18} className="text-[#5d8a73]" />}
            <span className="text-xs font-semibold font-['Outfit'] text-slate-700 leading-tight">{label}</span>
          </button>
        ))}
      </div>

      {shares === null && (
        <div className="flex justify-center py-6 text-slate-300"><Loader2 className="animate-spin" /></div>
      )}
      {shares !== null && shares.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-4">No links yet. Choose a document above to create one.</p>
      )}
      {shares !== null && shares.length > 0 && (
        <div className="space-y-2.5 max-h-[45vh] overflow-y-auto pr-1">
          {shares.map((s) => (
            <ShareRow key={s.id} share={s} onCopy={copy} onRevoke={revoke} />
          ))}
        </div>
      )}
    </Modal>
  );
}
